import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useQuery } from "@tanstack/react-query";
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Connection,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type OnSelectionChangeParams,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { useActivityEditor, useCardTemplates } from "@/hooks/useActivityEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Plus,
  Save,
  Send,
  Loader2,
  LayoutGrid,
  Trash2,
  X,
  Settings2,
  MousePointerClick,
  Clock,
  Minus,
  Lock,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import StepNodeComponent, { type StepNodeData } from "@/components/activities/graph/StepNode";
import TransitionEdgeComponent, { type TransitionEdgeData } from "@/components/activities/graph/TransitionEdge";
import StepConfigPanel, { type SelectedEdge } from "@/components/activities/graph/StepConfigPanel";
import type { ActivityCardInfo } from "@/components/activities/TransitionEditor";
import AddStepDialog from "@/components/activities/graph/AddStepDialog";
import CardGroupEditorDialog from "@/components/activities/graph/CardGroupEditorDialog";
import { useGraphLayout, getNewNodePosition } from "@/components/activities/graph/useGraphLayout";
import { PageHelpButton } from "@/components/PageHelpButton";

import type {
  EditorStep,
  EditorCardStep,
  EditorCardGroupStep,
  EditorTransition,
  TemplateContentFields,
  TemplateField,
} from "@/types/activity";
import { validateAllSteps } from "@/lib/validateCardContent";
import { cn } from "@/lib/utils";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { GuardedLink } from "@/components/GuardedLink";

/** Pre-populate content for true_false cards with Vrai/Faux options */
function initTrueFalseContent(
  templateCode: string | undefined,
  contentFields: unknown,
): Record<string, unknown> {
  if (templateCode !== "true_false") return {};
  const cf = contentFields as { fields?: TemplateField[] } | null;
  if (!cf?.fields) return {};
  const content: Record<string, unknown> = {};
  for (const field of cf.fields) {
    if (field.type === "array" && field.item?.fields?.length) {
      const subFields = field.item.fields;
      const hasId = subFields.some((f) => f.name === "id");
      if (hasId) {
        const hasLabel = subFields.some((f) => f.name === "label");
        const makeItem = (label: string, id: string) => {
          const item: Record<string, unknown> = {};
          subFields.forEach((f) => {
            if (f.type === "boolean") item[f.name] = false;
            else if (f.type === "integer") item[f.name] = 0;
            else if (f.type === "array") item[f.name] = [];
            else item[f.name] = "";
          });
          item.id = id;
          if (hasLabel) item.label = label;
          return item;
        };
        content[field.name] = [makeItem("Vrai", "true"), makeItem("Faux", "false")];
      }
    }
  }
  return content;
}

/* ─── React Flow node/edge type maps ─────────────────────── */

const nodeTypes = { stepNode: StepNodeComponent };
const edgeTypes = { transitionEdge: TransitionEdgeComponent };

/* ─── Helper: get label for step ─────────────────────────── */

function getCardContentTitle(content: Record<string, unknown>): string {
  for (const val of Object.values(content)) {
    if (typeof val === "string" && val.trim()) {
      const t = val.trim();
      return t.length > 30 ? t.slice(0, 30) + "…" : t;
    }
  }
  return "";
}

function extractCardOptions(
  card: EditorCardStep,
  tpl?: { code: string; content_fields?: unknown } | null,
): { id: string; label: string }[] | undefined {
  const code = card.templateCode || tpl?.code;

  // For single/multiple choice: extract from content.options
  if (code === "single_choice" || code === "multiple_choice") {
    const options = card.content?.options;
    if (!Array.isArray(options)) return undefined;
    return options
      .filter((o: any) => o && typeof o === "object" && o.id)
      .map((o: any) => ({ id: String(o.id), label: String(o.label || o.id) }));
  }

  // For true_false template
  if (code === "true_false") {
    return [
      { id: "true", label: "Vrai" },
      { id: "false", label: "Faux" },
    ];
  }

  // For likert: extract from content.options like single/multiple choice
  if (code === "likert") {
    const options = card.content?.options;
    if (!Array.isArray(options)) return undefined;
    return options
      .filter((o: any) => o && typeof o === "object" && o.id)
      .map((o: any) => ({ id: String(o.id), label: String(o.label || o.id) }));
  }

  // For other templates: look for select fields in content_fields
  const contentFields = tpl?.content_fields as { fields?: TemplateField[] } | null;
  if (contentFields?.fields) {
    for (const field of contentFields.fields) {
      if (field.type === "select" && field.options && field.options.length > 0) {
        return field.options.map((opt) => {
          if (typeof opt === "string") return { id: opt, label: opt };
          return { id: opt.key, label: opt.label || opt.key };
        });
      }
    }
  }

  return undefined;
}

function getStepLabel(s: EditorStep): string {
  if (s.type === "card") {
    const base = s.templateLabel || "Carte";
    const title = getCardContentTitle(s.content);
    return title ? `${base} | ${title.slice(0, 20)}` : base;
  }
  const t = (s as EditorCardGroupStep).title || "Groupe";
  return t.length > 25 ? t.slice(0, 22) + "…" : t;
}

/* ─── Position key: use stable DB ID when available ──────── */

function positionKey(step: EditorStep): string {
  return step.existingStepId || step.localId;
}

/* ─── Convert steps ↔ React Flow nodes/edges ─────────────── */

function stepsToNodes(
  steps: EditorStep[],
  firstStepLocalId: string | null,
  savedPositions: Record<string, { x: number; y: number }>,
  errorStepIds?: Set<string>,
): Node<StepNodeData>[] {
  return steps.map((step, i) => ({
    id: step.localId,
    type: "stepNode",
    position: savedPositions[positionKey(step)] ?? { x: (i % 3) * 280, y: Math.floor(i / 3) * 180 },
    data: {
      step,
      isFirstStep: step.localId === firstStepLocalId,
      isEndStep: step.transitions.length === 0,
      hasErrors: errorStepIds?.has(step.localId) ?? false,
    },
  }));
}

function stepsToEdges(steps: EditorStep[], activityCards: ActivityCardInfo[]): Edge<TransitionEdgeData>[] {
  const edges: Edge<TransitionEdgeData>[] = [];
  for (const step of steps) {
    for (const t of step.transitions) {
      // Resolve response labels for display
      let responseLabels: string[] | undefined;
      if (t.condition?.type === "response_value") {
        const cond = t.condition as any;
        const card = activityCards.find((c) => c.cardId === cond.card_id);
        const vals: string[] = cond.values ?? [];
        if (card?.options && vals.length > 0) {
          responseLabels = vals
            .map((id: string) => card.options!.find((o) => o.id === id)?.label || id)
            .filter(Boolean);
          if (vals.length >= 2 && cond.logic) {
            const logicLabel = cond.logic === "and" ? "ET" : "OU";
            responseLabels = [responseLabels.join(` ${logicLabel} `)];
          }
        }
      }
      edges.push({
        id: `${step.localId}__${t.toStepLocalId}__${t.orderIndex}`,
        source: step.localId,
        target: t.toStepLocalId,
        type: "transitionEdge",
        markerEnd: { type: MarkerType.ArrowClosed, color: "hsl(var(--border))" },
        data: { condition: t.condition, orderIndex: t.orderIndex, responseLabels },
      });
    }
  }
  return edges;
}

/* ─── Main component (inner, needs ReactFlowProvider) ────── */

function ActivityEditorInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pathId = searchParams.get("pathId");
  const isNew = !id || id === "new";
  const linkedToPathRef = useRef(false);
  const { data: templates } = useCardTemplates();

  const { formData, setFormData, steps, setSteps, isLoading, isSaving, isPublished, save } = useActivityEditor(
    isNew ? undefined : id,
  );

  // Fetch parent path title when in path context
  const { data: parentPath } = useQuery({
    queryKey: ["path-context", pathId],
    queryFn: async () => {
      const { data } = await supabase.from("path").select("title, path_type").eq("id", pathId!).maybeSingle();
      return data;
    },
    enabled: !!pathId,
    staleTime: 5 * 60 * 1000,
  });

  const parentPathType = parentPath?.path_type || null;

  const [groupEditorStepId, setGroupEditorStepId] = useState<string | null>(null);
  const [addStepDialog, setAddStepDialog] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [validationErrorIds, setValidationErrorIds] = useState<Set<string>>(new Set());
  const [validationErrorsMap, setValidationErrorsMap] = useState<
    Map<string, import("@/lib/validateCardContent").ValidationError[]>
  >(new Map());
  const [attempted, setAttempted] = useState(false);

  // Dirty tracking
  const isDirtyRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const markDirty = useCallback(() => {
    isDirtyRef.current = true;
    setDirty(true);
  }, []);

  // Auto-save (will be wired after handleSave is defined)
  const handleSaveRef = useRef<((publish: boolean) => Promise<boolean>) | null>(null);
  const autoSaveFn = useCallback(async () => {
    if (handleSaveRef.current) {
      await handleSaveRef.current(false);
    }
  }, []);
  const { triggerAutoSave, cancelAutoSave, isAutoSaving } = useAutoSave(autoSaveFn);

  // Wrap setFormData / setSteps to mark dirty + trigger auto-save
  const setFormDataDirty = useCallback(
    (data: typeof formData) => {
      markDirty();
      setFormData(data);
      triggerAutoSave();
    },
    [setFormData, markDirty, triggerAutoSave],
  );
  const setStepsDirty = useCallback(
    (s: EditorStep[]) => {
      markDirty();
      setSteps(s);
      triggerAutoSave();
    },
    [setSteps, markDirty, triggerAutoSave],
  );

  // Selection state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeData, setSelectedEdgeData] = useState<SelectedEdge | null>(null);

  // Node positions: keyed by localId for ReactFlow, remapped on save/load via positionKey
  const positionsRef = useRef<Record<string, { x: number; y: number }>>({});

  // Build initial positions from activity metadata
  const savedPositions = useMemo(() => {
    const meta = formData.metadata as Record<string, unknown> | null;
    const pos = (meta?.nodePositions as Record<string, { x: number; y: number }>) ?? {};
    return pos;
  }, [formData.metadata]);

  // Build activityCards for response_value condition selectors
  const activityCards: ActivityCardInfo[] = useMemo(() => {
    const cards: ActivityCardInfo[] = [];
    for (const step of steps) {
      if (step.type === "card") {
        const tpl = templates?.find((t) => t.id === step.templateId);
        const options = extractCardOptions(step, tpl);
        cards.push({
          cardId: step.existingCardId || step.localId,
          label: getStepLabel(step),
          templateCode: step.templateCode || tpl?.code || "",
          options,
        });
      } else {
        const group = step as EditorCardGroupStep;
        for (const card of group.cards) {
          const tpl = templates?.find((t) => t.id === card.templateId);
          const options = extractCardOptions(card, tpl);
          const contentTitle = getCardContentTitle(card.content);
          cards.push({
            cardId: card.existingCardId || card.localId,
            label: `${group.title || "Groupe"} › ${card.templateLabel || "Carte"}${contentTitle ? ` | ${contentTitle}` : ""}`,
            templateCode: card.templateCode || tpl?.code || "",
            options,
          });
        }
      }
    }
    return cards;
  }, [steps, templates]);

  // Build activityGroups for score condition selectors
  const activityGroups: import("@/components/activities/TransitionEditor").ActivityGroupInfo[] = useMemo(() => {
    return steps
      .filter((s) => s.type === "card_group")
      .map((s) => {
        const group = s as EditorCardGroupStep;
        return {
          groupId: group.existingGroupId || group.localId,
          label: group.title || "Groupe",
        };
      });
  }, [steps]);

  // React Flow state
  // Build initial nodes with vertical layout fallback
  const initialNodes = useMemo(
    () => {
      const raw = stepsToNodes(steps, formData.firstStepLocalId, savedPositions, validationErrorIds);
      // If no saved positions exist, use vertical layout
      const hasPositions = Object.keys(savedPositions).length > 0;
      if (!hasPositions && raw.length > 0) {
        return raw.map((n, i) => ({ ...n, position: { x: 0, y: i * 180 } }));
      }
      return raw;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, formData.firstStepLocalId],
  );
  const initialEdges = useMemo(() => stepsToEdges(steps, activityCards), [steps, activityCards]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync nodes/edges when steps change (preserve selection)
  const stepsKey = useMemo(
    () =>
      steps
        .map(
          (s) =>
            `${s.localId}:${s.transitions.map((t) => `${t.toStepLocalId}_${t.orderIndex}_${JSON.stringify(t.condition)}`).join("+")}`,
        )
        .join(","),
    [steps],
  );
  const prevStepsKeyRef = useRef(stepsKey);

  if (prevStepsKeyRef.current !== stepsKey) {
    prevStepsKeyRef.current = stepsKey;
    // Preserve selection state when updating nodes
    setNodes((currentNodes) => {
      const selectedIds = new Set(currentNodes.filter((n) => n.selected).map((n) => n.id));
      const newNodes = stepsToNodes(steps, formData.firstStepLocalId, savedPositions, validationErrorIds);
      // Keep current positions for nodes that already exist
      return newNodes.map((n) => {
        const existing = currentNodes.find((cn) => cn.id === n.id);
        return {
          ...n,
          position: existing?.position ?? positionsRef.current[n.id] ?? n.position,
          selected: selectedIds.has(n.id),
        };
      });
    });
    setEdges((currentEdges) => {
      const selectedIds = new Set(currentEdges.filter((e) => e.selected).map((e) => e.id));
      const newEdges = stepsToEdges(steps, activityCards);
      return newEdges.map((e) => ({
        ...e,
        selected: selectedIds.has(e.id),
      }));
    });
  }

  // Sync when firstStepLocalId changes
  const prevFirstStepRef = useRef(formData.firstStepLocalId);
  if (prevFirstStepRef.current !== formData.firstStepLocalId) {
    prevFirstStepRef.current = formData.firstStepLocalId;
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, isFirstStep: n.id === formData.firstStepLocalId },
      })),
    );
  }

  // Also sync node data when steps content/labels change (for live refresh)
  const stepsDataKey = useMemo(
    () =>
      steps
        .map((s) => {
          if (s.type === "card") return `${s.localId}:${s.templateLabel}:${JSON.stringify(s.content).slice(0, 80)}`;
          const g = s as EditorCardGroupStep;
          return `${s.localId}:${g.groupType}:${g.title}:${g.cards.length}`;
        })
        .join("|"),
    [steps],
  );
  const prevStepsDataKeyRef = useRef(stepsDataKey);
  if (prevStepsDataKeyRef.current !== stepsDataKey) {
    prevStepsDataKeyRef.current = stepsDataKey;
    // Update node data without resetting positions
    setNodes((nds) =>
      nds.map((n) => {
        const step = steps.find((s) => s.localId === n.id);
        if (!step) return n;
        return {
          ...n,
          data: {
            step,
            isFirstStep: n.id === formData.firstStepLocalId,
            isEndStep: step.transitions.length === 0,
            hasErrors: n.data.hasErrors ?? false,
          },
        };
      }),
    );
  }

  const { getAutoLayout } = useGraphLayout();

  // Track node positions on drag
  const handleNodesChange = useCallback(
    (changes: NodeChange<Node<StepNodeData>>[]) => {
      const removedIds = new Set<string>();
      for (const change of changes) {
        if (change.type === "position" && change.position) {
          positionsRef.current[change.id] = change.position;
          markDirty();
        }
        if (change.type === "remove") {
          removedIds.add(change.id);
        }
      }

      if (removedIds.size > 0) {
        const newSteps = steps
          .filter((s) => !removedIds.has(s.localId))
          .map((s) => ({
            ...s,
            transitions: s.transitions.filter((t) => !removedIds.has(t.toStepLocalId)),
          }));
        setStepsDirty(newSteps as EditorStep[]);
        if (selectedNodeId && removedIds.has(selectedNodeId)) {
          setSelectedNodeId(null);
        }
      }

      onNodesChange(changes);
    },
    [onNodesChange, markDirty, steps, setStepsDirty, selectedNodeId],
  );

  // Handle new connection (create transition)
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      const sourceStep = steps.find((s) => s.localId === connection.source);
      if (!sourceStep) return;

      const newTransition: EditorTransition = {
        toStepLocalId: connection.target,
        condition: null,
        orderIndex: sourceStep.transitions.length,
      };

      const newSteps = steps.map((s) => {
        if (s.localId === connection.source) {
          return { ...s, transitions: [...s.transitions, newTransition] };
        }
        return s;
      });
      setStepsDirty(newSteps as EditorStep[]);
    },
    [steps, setStepsDirty],
  );

  // Handle edge deletion
  const handleEdgesChange = useCallback(
    (changes: EdgeChange<Edge<TransitionEdgeData>>[]) => {
      const removedIds = new Set<string>();
      for (const change of changes) {
        if (change.type === "remove") removedIds.add(change.id);
      }

      if (removedIds.size > 0) {
        const newSteps = steps.map((s) => {
          const newTransitions = s.transitions.filter((t) => {
            const edgeId = `${s.localId}__${t.toStepLocalId}__${t.orderIndex}`;
            return !removedIds.has(edgeId);
          });
          if (newTransitions.length !== s.transitions.length) {
            return { ...s, transitions: newTransitions };
          }
          return s;
        });
        setStepsDirty(newSteps as EditorStep[]);
      }

      onEdgesChange(changes);
    },
    [steps, setStepsDirty, onEdgesChange],
  );

  // Selection handling
  const handleSelectionChange = useCallback(
    ({ nodes: selNodes, edges: selEdges }: OnSelectionChangeParams) => {
      if (selNodes.length > 0) {
        setSelectedNodeId(selNodes[0].id);
        setSelectedEdgeData(null);
        setSidebarTab("element");
      } else if (selEdges.length > 0) {
        const edge = selEdges[0];
        const sourceStep = steps.find((s) => s.localId === edge.source);
        if (sourceStep) {
          const tIdx = sourceStep.transitions.findIndex((t) => t.toStepLocalId === edge.target);
          setSelectedEdgeData({
            sourceStepLocalId: edge.source,
            transitionIndex: tIdx >= 0 ? tIdx : 0,
          });
        }
        setSelectedNodeId(null);
      } else {
        setSelectedNodeId(null);
        setSelectedEdgeData(null);
      }
    },
    [steps],
  );

  // Step CRUD
  const addCardStep = useCallback(
    (templateId: string) => {
      const template = templates?.find((t) => t.id === templateId);
      if (!template) return;
      const content = initTrueFalseContent(template.code, template.content_fields);
      const newStep: EditorCardStep = {
        type: "card",
        localId: crypto.randomUUID(),
        templateId,
        templateCode: template.code,
        templateLabel: template.label,
        content,
        transitions: [],
      };
      positionsRef.current[newStep.localId] = getNewNodePosition(nodes);
      const isFirst = steps.length === 0;
      setStepsDirty([...steps, newStep]);
      if (isFirst) {
        setFormData((prev) => ({ ...prev, firstStepLocalId: newStep.localId }));
      }
      setAddStepDialog(false);
      setSelectedNodeId(newStep.localId);
    },
    [templates, steps, setStepsDirty, nodes, setFormData],
  );

  const addCardGroupStep = useCallback(
    (groupType: string) => {
      const newStep: EditorCardGroupStep = {
        type: "card_group",
        localId: crypto.randomUUID(),
        title: "",
        groupType,
        cards: [],
        transitions: [],
      };
      positionsRef.current[newStep.localId] = getNewNodePosition(nodes);
      const isFirst = steps.length === 0;
      setStepsDirty([...steps, newStep]);
      if (isFirst) {
        setFormData((prev) => ({ ...prev, firstStepLocalId: newStep.localId }));
      }
      setAddStepDialog(false);
      setSelectedNodeId(newStep.localId);
    },
    [steps, setStepsDirty, nodes, setFormData],
  );

  const deleteSelectedStep = useCallback(() => {
    if (!selectedNodeId) return;
    const newSteps = steps.filter((s) => s.localId !== selectedNodeId);
    const cleaned = newSteps.map((s) => ({
      ...s,
      transitions: s.transitions.filter((t) => t.toStepLocalId !== selectedNodeId),
    }));
    setStepsDirty(cleaned as EditorStep[]);
    setSelectedNodeId(null);
  }, [selectedNodeId, steps, setStepsDirty]);

  // Update step by localId – uses functional updater to avoid stale closures
  const updateStep = useCallback(
    (localId: string, updates: Partial<EditorStep>) => {
      setSteps((prev) => {
        const newSteps = prev.map((s) => {
          if (s.localId === localId) {
            return { ...s, ...updates } as EditorStep;
          }
          return s;
        });
        return newSteps;
      });
      markDirty();
      triggerAutoSave();
    },
    [setSteps, markDirty, triggerAutoSave],
  );

  const updateTransitions = useCallback(
    (localId: string, transitions: EditorTransition[]) => {
      updateStep(localId, { transitions } as any);
    },
    [updateStep],
  );

  const addCardToGroup = useCallback(
    (stepLocalId: string, templateId: string) => {
      const template = templates?.find((t) => t.id === templateId);
      if (!template) return;
      const step = steps.find((s) => s.localId === stepLocalId) as EditorCardGroupStep | undefined;
      if (!step) return;
      const content = initTrueFalseContent(template.code, template.content_fields);
      const newCard: EditorCardStep = {
        type: "card",
        localId: crypto.randomUUID(),
        templateId,
        templateCode: template.code,
        templateLabel: template.label,
        content,
        transitions: [],
      };
      updateStep(stepLocalId, { cards: [...step.cards, newCard] } as any);
    },
    [templates, steps, updateStep],
  );

  const removeCardFromGroup = useCallback(
    (stepLocalId: string, cardIdx: number) => {
      const step = steps.find((s) => s.localId === stepLocalId) as EditorCardGroupStep | undefined;
      if (!step) return;
      updateStep(stepLocalId, { cards: step.cards.filter((_, i) => i !== cardIdx) } as any);
    },
    [steps, updateStep],
  );

  const updateGroupCard = useCallback(
    (stepLocalId: string, cardIdx: number, updates: Partial<EditorCardStep>) => {
      const step = steps.find((s) => s.localId === stepLocalId) as EditorCardGroupStep | undefined;
      if (!step) return;
      const newCards = [...step.cards];
      newCards[cardIdx] = { ...newCards[cardIdx], ...updates };
      updateStep(stepLocalId, { cards: newCards } as any);
    },
    [steps, updateStep],
  );

  const getTemplateFields = useCallback(
    (templateId: string): TemplateContentFields | null => {
      const template = templates?.find((t) => t.id === templateId);
      if (!template?.content_fields) return null;
      return template.content_fields as unknown as TemplateContentFields;
    },
    [templates],
  );

  // Auto-layout
  const handleAutoLayout = useCallback(() => {
    const laid = getAutoLayout(nodes) as Node<StepNodeData>[];
    setNodes(laid);
    for (const n of laid) {
      positionsRef.current[n.id] = n.position;
    }
    markDirty();
  }, [nodes, setNodes, getAutoLayout, markDirty]);

  // Build position map with stable keys for persistence
  const buildPositionMetadata = useCallback(() => {
    const nodePositions: Record<string, { x: number; y: number }> = {};
    // Read positions from the actual React Flow nodes (source of truth)
    for (const node of nodes) {
      const step = steps.find((s) => s.localId === node.id);
      if (step) {
        nodePositions[positionKey(step)] = node.position;
      }
    }
    return {
      ...(formData.metadata || {}),
      nodePositions,
    };
  }, [nodes, steps, formData.metadata]);

  // Save with positions persisted in metadata
  // Compute total estimated duration from cards (default 1 min each)
  const computeTotalDuration = useCallback(() => {
    let total = 0;
    for (const step of steps) {
      if (step.type === "card") {
        total += step.estimatedDurationMinutes ?? 1;
      } else {
        for (const card of step.cards) {
          total += card.estimatedDurationMinutes ?? 1;
        }
      }
    }
    return total;
  }, [steps]);

  // Display duration: forced value or computed
  const displayDuration = formData.isDurationForced
    ? (formData.estimatedDurationMinutes ?? 0)
    : computeTotalDuration();

  const handleSave = useCallback(
    async (publish: boolean): Promise<boolean> => {
      cancelAutoSave();
      setAttempted(true);
      // Validate title
      if (!formData.title.trim()) {
        toast.error("Le titre est requis");
        return false;
      }
      // Validate all card content before saving
      const errors = validateAllSteps(steps, getTemplateFields);
      if (errors.size > 0) {
        setValidationErrorIds(new Set(errors.keys()));
        setValidationErrorsMap(errors);
        setNodes((currentNodes) =>
          currentNodes.map((n) => ({
            ...n,
            data: { ...n.data, hasErrors: errors.has(n.id) },
          })),
        );
        const totalErrors = Array.from(errors.values()).reduce((sum, e) => sum + e.length, 0);
        toast.error(`${totalErrors} erreur(s) de validation dans ${errors.size} carte(s)`, {
          description: "Corrigez les champs obligatoires avant de sauvegarder.",
        });
        return false;
      }

      // Clear previous validation errors
      setValidationErrorIds(new Set());
      setValidationErrorsMap(new Map());
      setNodes((currentNodes) =>
        currentNodes.map((n) => ({
          ...n,
          data: { ...n.data, hasErrors: false },
        })),
      );

      const metadataWithPositions = buildPositionMetadata();
      const totalDuration = formData.isDurationForced
        ? (formData.estimatedDurationMinutes ?? 0)
        : computeTotalDuration();
      const updatedFormData = { ...formData, metadata: metadataWithPositions, estimatedDurationMinutes: totalDuration };
      setFormData(updatedFormData);
      const savedActivity = await save(publish, metadataWithPositions, totalDuration);
      isDirtyRef.current = false;
      setDirty(false);

      // If created from a path editor, auto-link the new activity to the path
      if (isNew && pathId && pathId !== "new" && savedActivity && !linkedToPathRef.current) {
        linkedToPathRef.current = true;
        const { data: existingPAs } = await supabase
          .from("path_activity")
          .select("order_index")
          .eq("path_id", pathId)
          .order("order_index", { ascending: false })
          .limit(1);
        const nextOrder = existingPAs && existingPAs.length > 0 ? existingPAs[0].order_index + 1 : 0;
        await supabase.from("path_activity").insert({
          path_id: pathId,
          activity_id: savedActivity.id,
          order_index: nextOrder,
        });
      }
      return true;
    },
    [
      formData,
      setFormData,
      save,
      buildPositionMetadata,
      isNew,
      pathId,
      steps,
      getTemplateFields,
      setNodes,
      cancelAutoSave,
    ],
  );

  // Wire handleSaveRef for auto-save
  handleSaveRef.current = handleSave;

  // Determine the "back" destination
  const backUrl = pathId ? `/paths/${pathId}` : "/activities";

  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(backUrl);
    }
  }, [navigate, backUrl]);

  // Register navigation guard for dirty state
  const { registerGuard } = useNavigationGuard();
  const pendingNavigationRef = useRef<string | null>(null);

  useEffect(() => {
    const unregister = registerGuard(() => isDirtyRef.current);
    return unregister;
  }, [registerGuard]);

  // Also block browser close/refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Close handler (close button)
  const handleClose = useCallback(() => {
    if (isDirtyRef.current) {
      pendingNavigationRef.current = backUrl;
      setConfirmClose(true);
    } else {
      goBack();
    }
  }, [goBack, backUrl]);

  // Called from sidebar/external navigation via context
  const handleBlockedNavigation = useCallback((targetUrl: string) => {
    pendingNavigationRef.current = targetUrl;
    setConfirmClose(true);
  }, []);

  // Expose blocked navigation handler via ref on the guard context
  const { blockedHandlerRef } = useNavigationGuard();
  useEffect(() => {
    blockedHandlerRef.current = handleBlockedNavigation;
    return () => { blockedHandlerRef.current = null; };
  }, [handleBlockedNavigation, blockedHandlerRef]);

  const handleCloseDiscard = useCallback(() => {
    setConfirmClose(false);
    const target = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    isDirtyRef.current = false;
    setDirty(false);
    if (target) {
      navigate(target);
    } else {
      goBack();
    }
  }, [navigate, goBack]);

  const handleCloseSave = useCallback(async () => {
    setConfirmClose(false);
    const success = await handleSave(false);
    if (success) {
      const target = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      isDirtyRef.current = false;
      setDirty(false);
      if (target) {
        navigate(target);
      } else {
        goBack();
      }
    }
  }, [handleSave, navigate, goBack]);

  const handleCloseContinue = useCallback(() => {
    setConfirmClose(false);
    pendingNavigationRef.current = null;
  }, []);

  const selectedStep = selectedNodeId ? (steps.find((s) => s.localId === selectedNodeId) ?? null) : null;

  const hasElementSelection = selectedStep !== null || selectedEdgeData !== null;

  // Active sidebar tab: auto-switch to "element" when something is selected
  const [sidebarTab, setSidebarTab] = useState<string>("params");
  const prevHasSelectionRef = useRef(hasElementSelection);
  if (hasElementSelection && !prevHasSelectionRef.current) {
    // Selection just appeared → switch to element tab
    setSidebarTab("element");
  }
  if (!hasElementSelection && prevHasSelectionRef.current) {
    // Selection cleared → switch back to params
    setSidebarTab("params");
  }
  prevHasSelectionRef.current = hasElementSelection;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex flex-col border-b border-border bg-card shrink-0">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <PageHelpButton tourKey="activity-editor" />
            <div data-tour="activity-editor-title">
              {pathId ? (
                <>
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <GuardedLink to="/paths">Parcours</GuardedLink>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <GuardedLink to={`/paths/${pathId}`}>{parentPath?.title || "Parcours"}</GuardedLink>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        <BreadcrumbPage>{isNew ? "Nouvelle activité" : formData.title || "Activité"}</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                  <h1 className="text-lg font-bold tracking-tight">
                    {isPublished
                      ? "Visualiser l'activité du parcours"
                      : isNew
                        ? "Nouvelle activité du parcours"
                        : "Modifier l'activité du parcours"}{" "}
                    "{parentPath?.title}"
                  </h1>
                </>
              ) : (
                <h1 className="text-lg font-bold tracking-tight">
                  {isPublished ? "Visualiser l'activité" : isNew ? "Nouvelle activité" : "Modifier l'activité"}
                </h1>
              )}
            </div>
            {isPublished && (
              <Badge variant="default" className="ml-2">
                Publiée
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5" data-tour="activity-editor-duration">
              <Badge variant="outline" className="gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {formData.isDurationForced ? (
                  <span className="flex items-center gap-1">
                    <span>Durée forcée :</span>
                    {!isPublished && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 -my-1"
                        onClick={() => setFormDataDirty({
                          ...formData,
                          estimatedDurationMinutes: Math.max(0, (formData.estimatedDurationMinutes ?? 1) - 1),
                        })}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                    )}
                    <span className="font-medium w-4 text-center">{formData.estimatedDurationMinutes ?? 0}</span>
                    {!isPublished && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 -my-1"
                        onClick={() => setFormDataDirty({
                          ...formData,
                          estimatedDurationMinutes: (formData.estimatedDurationMinutes ?? 0) + 1,
                        })}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    )}
                    <span>min</span>
                  </span>
                ) : (
                  <span>Durée estimée : {displayDuration} min</span>
                )}
              </Badge>
              {!isPublished && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title={formData.isDurationForced ? "Revenir au calcul automatique" : "Forcer la durée manuellement"}
                  onClick={() => {
                    const newForced = !formData.isDurationForced;
                    setFormDataDirty({
                      ...formData,
                      isDurationForced: newForced,
                      estimatedDurationMinutes: newForced ? computeTotalDuration() : null,
                    });
                  }}
                >
                  {formData.isDurationForced ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5 text-muted-foreground" />}
                </Button>
              )}
            </div>
            {!isPublished && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSave(false)}
                  disabled={isSaving || !dirty}
                  className={isAutoSaving ? "animate-pulse" : ""}
                >
                  {isSaving || isAutoSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {isAutoSaving ? "Sauvegarde…" : "Sauvegarder"}
                </Button>
                {!pathId && (
                  <Button size="sm" onClick={() => setConfirmPublish(true)} disabled={isSaving}>
                    <Send className="mr-2 h-4 w-4" />
                    Publier
                  </Button>
                )}
              </>
            )}
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="mr-1 h-4 w-4" />
              Fermer
            </Button>
          </div>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 min-h-0">
        {/* Canvas */}
        <div className="flex-1 relative" data-tour="activity-editor-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={isPublished ? undefined : handleNodesChange}
            onEdgesChange={isPublished ? undefined : handleEdgesChange}
            onConnect={isPublished ? undefined : handleConnect}
            onSelectionChange={handleSelectionChange}
            onNodeClick={() => setSidebarTab("element")}
            onNodeDoubleClick={(_event, node) => {
              const step = steps.find((s) => s.localId === node.id);
              if (step?.type === "card_group") setGroupEditorStepId(node.id);
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            defaultEdgeOptions={{
              type: "transitionEdge",
              markerEnd: { type: MarkerType.ArrowClosed, color: "hsl(var(--border))" },
            }}
            nodesDraggable={!isPublished}
            nodesConnectable={!isPublished}
            deleteKeyCode={isPublished ? [] : ["Backspace", "Delete"]}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="hsl(var(--border))" />
            <Controls className="!bg-card !border-border !shadow-sm" />
            <MiniMap
              className="!bg-card !border-border"
              nodeColor="hsl(var(--primary))"
              maskColor="hsl(var(--background) / 0.7)"
            />
          </ReactFlow>

          {!isPublished && (
            <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
              <button
                className="group/add inline-flex items-center h-8 rounded-md border border-input bg-card px-2 text-sm text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={() => setAddStepDialog(true)}
                title="Ajouter une étape"
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span className="ml-1.5 max-w-0 overflow-hidden whitespace-nowrap transition-[max-width] duration-300 ease-in-out group-hover/add:max-w-[8rem] text-xs">
                  Ajouter
                </span>
              </button>
              <button
                className="group/layout inline-flex items-center h-8 rounded-md border border-input bg-card px-2 text-sm text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={handleAutoLayout}
                title="Auto-layout"
              >
                <LayoutGrid className="h-4 w-4 shrink-0" />
                <span className="ml-1.5 max-w-0 overflow-hidden whitespace-nowrap transition-[max-width] duration-300 ease-in-out group-hover/layout:max-w-[8rem] text-xs">
                  Auto-layout
                </span>
              </button>
              {selectedNodeId && (
                <button
                  className="group/del inline-flex items-center h-8 rounded-md border border-input bg-card px-2 text-sm text-destructive shadow-sm transition-colors hover:bg-destructive/10"
                  onClick={deleteSelectedStep}
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4 shrink-0" />
                  <span className="ml-1.5 max-w-0 overflow-hidden whitespace-nowrap transition-[max-width] duration-300 ease-in-out group-hover/del:max-w-[8rem] text-xs">
                    Supprimer
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabbed sidebar */}
        <div className="w-[500px] shrink-0 border-l border-border bg-card flex flex-col" data-tour="activity-editor-sidebar">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="flex flex-col h-full">
            <TabsList className="w-full rounded-none border-b border-border bg-transparent p-0 h-auto">
              <TabsTrigger
                value="params"
                className={cn(
                  "flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 gap-1.5",
                  attempted && !formData.title.trim() && "text-destructive data-[state=active]:text-destructive"
                )}
              >
                <Settings2 className="h-4 w-4" />
                Paramètres
              </TabsTrigger>
              <TabsTrigger
                value="element"
                disabled={!hasElementSelection}
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 gap-1.5"
              >
                <MousePointerClick className="h-4 w-4" />
                Élément
              </TabsTrigger>
            </TabsList>

            <TabsContent value="params" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-4 space-y-4">
                  <div className="space-y-1.5">
                    <Label className={attempted && !formData.title.trim() ? "text-destructive" : ""}>
                      Titre <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormDataDirty({ ...formData, title: e.target.value })}
                      onBlur={triggerAutoSave}
                      placeholder="Titre de l'activité"
                      className={attempted && !formData.title.trim() ? "border-destructive ring-destructive" : ""}
                      disabled={isPublished}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Résumé</Label>
                    <Textarea
                      value={formData.summary}
                      onChange={(e) => setFormDataDirty({ ...formData, summary: e.target.value })}
                      onBlur={triggerAutoSave}
                      placeholder="Description courte..."
                      rows={3}
                      disabled={isPublished}
                    />
                  </div>
                  {steps.length > 0 && (
                    <div className="space-y-1.5">
                      <Label>Étape de départ</Label>
                      <Select
                        value={formData.firstStepLocalId || ""}
                        onValueChange={(v) => setFormDataDirty({ ...formData, firstStepLocalId: v || null })}
                        disabled={isPublished}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner l'étape de départ..." />
                        </SelectTrigger>
                        <SelectContent>
                          {steps.map((s) => (
                            <SelectItem key={s.localId} value={s.localId}>
                              {getStepLabel(s)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="element" className="flex-1 m-0 overflow-hidden">
              {hasElementSelection ? (
                <StepConfigPanel
                  selectedStep={selectedStep}
                  allSteps={steps}
                  selectedEdge={selectedEdgeData}
                  templates={templates || []}
                  getTemplateFields={getTemplateFields}
                  activityCards={activityCards}
                  activityGroups={activityGroups}
                  validationErrors={selectedStep ? validationErrorsMap.get(selectedStep.localId) : undefined}
                  isDurationForced={formData.isDurationForced}
                  pathType={parentPathType}
                  readOnly={isPublished}
                  onUpdateStep={updateStep}
                  onAddCardToGroup={addCardToGroup}
                  onRemoveCardFromGroup={removeCardFromGroup}
                  onUpdateGroupCard={updateGroupCard}
                  onUpdateTransitions={updateTransitions}
                  onClose={() => {
                    setSelectedNodeId(null);
                    setSelectedEdgeData(null);
                  }}
                  onOpenGroupEditor={(localId) => setGroupEditorStepId(localId)}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6 text-center">
                  <MousePointerClick className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm">Sélectionnez un élément sur le graphe pour le configurer ici.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialogs */}
      <AddStepDialog
        open={addStepDialog}
        onClose={() => setAddStepDialog(false)}
        templates={templates || []}
        onAddCard={addCardStep}
        onAddGroup={addCardGroupStep}
      />

      {/* Card Group Editor Dialog */}
      {groupEditorStepId && (() => {
        const groupStep = steps.find((s) => s.localId === groupEditorStepId) as EditorCardGroupStep | undefined;
        if (!groupStep || groupStep.type !== "card_group") return null;
        return (
          <CardGroupEditorDialog
            open={true}
            onClose={() => setGroupEditorStepId(null)}
            step={groupStep}
            templates={templates || []}
            getTemplateFields={getTemplateFields}
            onUpdateStep={(localId, updates) => updateStep(localId, updates as any)}
            onAddCard={addCardToGroup}
            onRemoveCard={removeCardFromGroup}
            onUpdateCard={updateGroupCard}
            pathType={parentPathType}
            readOnly={isPublished}
          />
        );
      })()}

      <AlertDialog open={confirmPublish} onOpenChange={setConfirmPublish}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publier cette activité ?</AlertDialogTitle>
            <AlertDialogDescription>
              Une activité publiée devient immuable. Vous devrez créer une nouvelle version pour la modifier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmPublish(false);
                handleSave(true);
              }}
            >
              Publier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClose} onOpenChange={(open) => { if (!open) handleCloseContinue(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non sauvegardées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non sauvegardées. Que souhaitez-vous faire ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction onClick={handleCloseSave} disabled={isSaving} className="w-full">
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sauvegarder et fermer
            </AlertDialogAction>
            <Button variant="outline" onClick={handleCloseDiscard} className="w-full">
              Annuler les modifications
            </Button>
            <AlertDialogCancel onClick={handleCloseContinue} className="w-full">
              Continuer l'édition
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Wrapped with ReactFlowProvider ─────────────────────── */

export default function ActivityEditor() {
  return (
    <ReactFlowProvider>
      <ActivityEditorInner />
    </ReactFlowProvider>
  );
}
